import React, { useEffect, useState, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import TwoLineCardSkeleton from "../components/SkeletonCards/TwoLineCardSkeleton";
import { Competition } from "../types";
import TextInput from "../components/ui/Input/TextInput";
import EntityAddUpdateForm from "../components/EntityAddUpdateForm/EntityAddUpdateForm";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from '../components/ui/Input/Input.module.css'; // Adjust the relative path
import { format } from 'date-fns';
import YearDropDown from "../components/YearDropDown.tsx";

const Competitions: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState<string>("");
  const [startDate, setStartDate] = useState<string | Date>(""); // Default to an empty string
  const [endDate, setEndDate] = useState<string | Date>("");

  const [competitionId, setCompetitionId] = useState<number>(NaN);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUpdateActive, setIsUpdateActive] = useState(false);
  const defaultYear = parseInt(import.meta.env.VITE_DEFAULT_YEAR);
  const getStoredYear = (): number | null => {
    const stored = localStorage.getItem('selectedCompetitionYear');
    return stored ? parseInt(stored) : defaultYear;
  };
  const [selectedYear, setSelectedYear] = useState<number | null>(getStoredYear());
  const {
    data: competitions,
    setData: setCompetitions,
    errorContent,
    isLoading,
    fetchData,
    hasFetched,
  } = useFetch({
    url: selectedYear
      ? `${import.meta.env.VITE_API_URL}/competitions/year/${selectedYear}`
      : `${import.meta.env.VITE_API_URL}/competitions`,
    method: "GET",
  });

  const {
    data: allCompetitions,
    fetchData: fetchAllCompetitions,
  } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/competitions`,
    method: "GET",
  });
  
  useEffect(() => {
    fetchAllCompetitions();
  }, []);


  const yearOptions = useMemo(() => {
    const availableYears = [];
    
    if (allCompetitions && allCompetitions.length > 0) {
      const years = allCompetitions.map((comp: Competition) => {
        const date = new Date(comp.start_date);
        return date.getFullYear();
      });
      
      const uniqueYears = [...new Set(years) as Set<number>].sort((a: number, b: number) => b - a);
      availableYears.push(...uniqueYears);
    }
    
    if (!availableYears.includes(defaultYear)) {
      availableYears.unshift(defaultYear);
    }
    
    return availableYears;
  }, [allCompetitions]);


  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  if (errorContent) return errorContent;

  if (!hasFetched) {
    return null; // Prevent rendering until fetch is complete
  }

  const clearInputFields = () => {
    setTitle("");
    setStartDate("");
    setEndDate("");
  };

  const handleSubmitCompetition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrorMessage("Title is empty.");
      return;
    }
    
    if (!startDate || isNaN(new Date(startDate).getTime())) {
      setErrorMessage("Start date is empty.");
      return;
    }
    
    if (!endDate || isNaN(new Date(endDate).getTime())) {
      setErrorMessage("End date is empty.");
      return;
    }
    
    // If all validations pass, clear any error message
    setErrorMessage(null);
     // Convert the start and end dates to UTC using date-fns format
    const formattedStartDate = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEndDate = format(new Date(endDate), 'yyyy-MM-dd');
    
    const competitionData = {
      title: title,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };
    console.log("competitionData " , competitionData);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/competitions/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(competitionData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Competition added successfully!");
        setErrorMessage(null);

        const updatedCompetitions = [...competitions, result.data];
        //console.log(updatedCompetitions);
        setCompetitions(updatedCompetitions);
        
        const newCompetitionYear = new Date(result.data.start_date).getFullYear();
        setSelectedYear(newCompetitionYear);
        localStorage.setItem('selectedCompetitionYear', newCompetitionYear.toString());
        fetchAllCompetitions();
      } else {

        if (result.success === false && result.message) {
          setErrorMessage("Error adding competition: " + result.message);
        } else {
          setErrorMessage(result?.errors?.[0]?.error ?? "Error adding competition");
        }
        setSuccessMessage(null);
      }
      clearInputFields();
    } catch (err) {
      setErrorMessage("Failed to add competition, please try again.");
      setSuccessMessage(null);
    }
  };

  const handleDeleteCompetition = async (competitionId: number) => {
    console.log(competitionId);
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Competition?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/competitions/delete/${competitionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSuccessMessage("Competition deleted successfully!");
        setErrorMessage(null);

        setCompetitions((prevData: Competition[]) =>
          prevData.filter((competition) => competition.id !== competitionId)
        );
        fetchAllCompetitions();
        if (competitions.length === 1) {
          setSelectedYear(defaultYear);
          localStorage.setItem('selectedCompetitionYear', defaultYear.toString());
        }
      } else {
        let errorMessage = "Error deleting Competition: "; let errorMessagedetail;
        const result = await response.json();
        if (result.error.error_message?.includes("Cannot delete or update a parent row")) {
          errorMessagedetail = "This competition contains child records, please remove them first.";
        } else {
          errorMessagedetail = result.message || errorMessage;
        }
        errorMessage += errorMessagedetail;
        setErrorMessage(errorMessage);
        setSuccessMessage(null);
      }
    } catch (err) {
      setErrorMessage("Failed to delete competition, please try again.");
      setSuccessMessage(null);
    }
  };

  const populateFiledsForUpdate = (competition: {
    title: string;
    start_date: string;
    end_date: string;
  }) => {
    const formattedStartDate = new Date(competition.start_date);
    const formattedEndDate = new Date(competition.end_date);

    /*const formattedStartDateString = formattedStartDate
      .toISOString()
      .split("T")[0];
    const formattedEndDateString = formattedEndDate.toISOString().split("T")[0];*/

    setTitle(competition.title);
    setStartDate(formattedStartDate);
    setEndDate(formattedEndDate);

    setIsUpdateActive(true);
  };

  const sendUpdatedCompetitionData = async (competitionId: number) => {
    
    const formattedStartDate = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEndDate = format(new Date(endDate), 'yyyy-MM-dd');
    
    const competitionData = {
      title: title,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/competitions/update/${competitionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(competitionData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Competition updated successfully!");
        setErrorMessage(null);

        // Update the local users state
        setCompetitions((prevCompetition: any) =>
          prevCompetition.map((competition: any) =>
            competition.id === competitionId
              ? { ...competition, ...result.data }
              : competition
          )
        );
        const newCompetitionYear = new Date(result.data.start_date).getFullYear();
        setSelectedYear(newCompetitionYear);
        fetchAllCompetitions();
        localStorage.setItem('selectedCompetitionYear', newCompetitionYear.toString());
      } else {
        setErrorMessage(result.errors[0].error || "Error updating competition");
        setSuccessMessage(null);
      }
    } catch (err) {
      setErrorMessage("Failed to update competition, please try again." );
      setSuccessMessage(null);
    }
  };

  const handleUpdateCompetition = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendUpdatedCompetitionData(competitionId);
    clearInputFields();
    setIsUpdateActive(false);
    setCompetitionId(NaN);
  };

 
  const isAdmin = JSON.parse(localStorage.getItem("user") || "{}")?.current_role === 1;

/*
  const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;
  const canViewEventsBoxlink = (currentRole !== 1);
  const canSeeEventsButton = currentRole == 1 ;
  const canEdit = currentRole == 1;
  const canDelete = currentRole == 1;

        <section>
        {currentRole == "1" ? (
          <div className="form-container">
*/
  const canView = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "competitions")?.ModulesPermissions?.view;
  const canAdd = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "competitions")?.ModulesPermissions?.add;
  
  const canEdit =  (isAdmin && !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "competitions")?.ModulesPermissions?.update);
  const canDelete = (isAdmin && !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "competitions")?.ModulesPermissions?.delete);

  const canViewEventsBoxlink = (!isAdmin && canView);
  const canSeeEventsButton = (isAdmin && canView);

  return (
    <>
      <section>
        {(isAdmin && canAdd) ? (
          <div className="form-container">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 className="section__heading">Add/Edit Competition</h2>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: "1rem" }}>
                <YearDropDown selectedYear={selectedYear}
                  onYearChange={(year) => {
                    setSelectedYear(year);
                    localStorage.setItem('selectedCompetitionYear', year.toString());
                  }} 
                  yearOptions={yearOptions}
                />
              </div>
            </div>
            <EntityAddUpdateForm
              handleUserSubmit={
                isUpdateActive
                  ? handleUpdateCompetition
                  : handleSubmitCompetition
              }
            >
              <TextInput
                placeholder="Title"
                value={title}
                onChange={(e) => {
                  setErrorMessage(null);
                  setSuccessMessage(null);
                  setTitle(e.target.value);
                }}
              />

            <DatePicker
                    id="startDate"
                    selected={startDate ? new Date(startDate) : null}
                    autoComplete="off"
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Start Date"
                    isClearable={false}
                    customInput={<input className={styles.singleInput} style={{ width: "auto", outline: "0" }}/>}
                    onChange={(date) => {
                      setErrorMessage(null);
                      setSuccessMessage(null);
                      if (date) {
                        setStartDate(date);
                      }
                    }}
            />

            <DatePicker
                    id="endDate"
                    selected={endDate ? new Date(endDate) : null}
                    autoComplete="off"
                    dateFormat="MM/dd/yyyy"
                    placeholderText="End Date"
                    isClearable={false}
                    customInput={<input className={styles.singleInput} style={{ width: "auto" }}/>}
                    onChange={(date) => {
                      setErrorMessage(null);
                      setSuccessMessage(null);
                      if (date) {
                        setEndDate(date);
                      }
                    }}
            />
            </EntityAddUpdateForm>
            {successMessage && (
              <p style={{ color: "green" }}>{successMessage}</p>
            )}
            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
          </div>
        ) :
        <div className="container">
          <YearDropDown selectedYear={selectedYear}
                  onYearChange={(year) => {
                    setSelectedYear(year);
                    localStorage.setItem('selectedCompetitionYear', year.toString());
                  }} 
                  yearOptions={yearOptions}
                />
        </div>}
        <div className="container">
          <h3 className="section__heading"></h3><br/>
          <div className="competitions__mainBox">
            {isLoading ? (
              <>
                <TwoLineCardSkeleton
                  cardsCount={1}
                  baseColor="#cae2f9"
                  highlightColor="#eee"
                />
              </>
            ) : (
              competitions.map((competition: Competition) => {
                return (
                  <div
                    key={competition.id}
                    className="small-card cursor-pointer scale--md"
                    onClick={() => {
                      if (canViewEventsBoxlink) {
                        navigate(`/events/${competition.id}`);
                      }
                    }}
                  >

                    <h3 className="comp-small-card__title">
                      {competition.title}
                    </h3>
                    <p className="small-card__dates">
                      {competition.start_date} - {competition.end_date}
                    </p>
                    
                    <div className="small-card-btnsBoxCompetition">
                      {canSeeEventsButton && (
                      <button
                        className="action-btn"
                        onClick={() => navigate(`/events/${competition.id}`)}
                      >
                        Events
                      </button>
                      )}

                      {canEdit && (
                        <button
                          className="action-btn"
                          onClick={() => {
                            setCompetitionId(competition.id);
                            populateFiledsForUpdate({
                              title: competition.title,
                              start_date: competition.start_date,
                              end_date: competition.end_date,
                            });
                          }}
                        >
                          Edit
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="action-btn"
                          onClick={() => handleDeleteCompetition(competition.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Competitions;